import { useMemo, useState, type ReactNode } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

interface Props<T> {
  value: T | null;
  options: {
    value: T;
    label: ReactNode;
    disabled?: boolean;
    className?: string;
  }[];
  disabled?: boolean;
  mode?: "static" | "expandable";
  variant?: "dark" | "light";
  onChange: (value: T) => void;
}

export const ButtonGroup = <T extends string>({
  value,
  options,
  disabled,
  mode = "static",
  variant = "dark",
  onChange,
}: Props<T>) => {
  const [isHovering, setIsHovering] = useState(false);

  const visibleOptions = useMemo(() => {
    if (mode === "static" || isHovering) {
      return options;
    }

    const filteredOptions = options.filter((e) => e.value === value);

    if (filteredOptions.length > 0) {
      return filteredOptions;
    }

    if (options[0]) {
      return [options[0]];
    }

    return [];
  }, [mode, options, isHovering]);

  return (
    <div
      className="flex items-center text-sm"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {visibleOptions.map((option) => {
        const active = option.value === value;
        const optionDisabled = disabled || option.disabled;

        return (
          <button
            key={option.value}
            disabled={optionDisabled}
            className={twMerge(
              "flex-1 bg-v2-primary h-8 border first:rounded-l-md last:rounded-r-md mr-[-1px] text-v2-primary",
              variant === "dark" ? "border-v2-secondary" : "border-v2-tertiary",
              "hover:z-[1] active:border-v2-action",
              "active:shadow-[inset_4px_4px_4px_#0000001A] truncate px-2",
              clsx({
                "hover:bg-v2-action-tertiary hover:border-v2-secondary":
                  !active,
                "font-semibold text-white border-v2-action": active,
                [variant === "dark"
                  ? "bg-v2-action text-white border-v2-action"
                  : "bg-v2-highlight-information text-v2-action border-v2-tertiary"]:
                  active,
                "bg-v2-gray/10 border-v2-disable text-v2-disable":
                  optionDisabled && variant === "dark",
                "hover:bg-v2-gray/10 hover:border-v2-disable active:border-v2-disable active:shadow-none":
                  optionDisabled && variant === "dark",
                "bg-v2-gray/10 border-v2-tertiary text-v2-gray/100":
                  optionDisabled && variant === "light",
                "hover:bg-v2-gray/10 hover:border-v2-tertiary active:border-v2-tertiary active:shadow-none":
                  optionDisabled && variant === "light",
              }),
              option.className,
            )}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
